/**
 * @file DiagnosticContext.js
 * @description Contexto para la gestión de herramientas de diagnóstico
 * Implementa principio de inversión de dependencias (SOLID) para desacoplar
 * componentes de UI y lógica de diagnóstico
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

// Crear contexto con valores iniciales
const DiagnosticContext = createContext({
  showCorsProxyDiagnostic: false,
  toggleCorsProxyDiagnostic: () => {},
  isDiagnosticEnabled: false,
  diagnosticPermissions: {}
});

/**
 * Proveedor del contexto de diagnóstico
 * @param {Object} props - Propiedades del componente
 * @param {React.ReactNode} props.children - Componentes hijos
 */
export function DiagnosticProvider({ children }) {
  // Estado para controlar la visibilidad del diagnóstico CORS
  const [showCorsProxyDiagnostic, setShowCorsProxyDiagnostic] = useState(false);
  
  // Estado para guardar los permisos de diagnóstico del usuario
  const [diagnosticPermissions, setDiagnosticPermissions] = useState({
    canViewCorsProxy: false,
    canViewSyncLogs: false,
    canViewApiLogs: false,
    isAdmin: false
  });
  
  /**
   * Determina si el usuario tiene permisos de diagnóstico basado en su rol
   * @param {Object} userData - Datos del usuario actual
   */
  const updateDiagnosticPermissions = useCallback((userData) => {
    // Por defecto, solo administradores pueden ver diagnósticos
    const isAdmin = userData?.role === 'admin' || 
                   userData?.rol === 'admin' ||
                   userData?.isAdmin === true;
    
    // Configurar permisos específicos
    setDiagnosticPermissions({
      canViewCorsProxy: isAdmin,
      canViewSyncLogs: isAdmin,
      canViewApiLogs: isAdmin,
      isAdmin
    });
  }, []);
  
  // Función para mostrar u ocultar el diagnóstico CORS
  const toggleCorsProxyDiagnostic = useCallback(() => {
    setShowCorsProxyDiagnostic(prev => !prev);
  }, []);
  
  // Calcular si alguna herramienta de diagnóstico está habilitada
  const isDiagnosticEnabled = Object.values(diagnosticPermissions).some(Boolean);
  
  // Valor del contexto que se proporcionará
  const contextValue = {
    showCorsProxyDiagnostic,
    toggleCorsProxyDiagnostic,
    isDiagnosticEnabled,
    diagnosticPermissions,
    updateDiagnosticPermissions
  };

  return (
    <DiagnosticContext.Provider value={contextValue}>
      {children}
    </DiagnosticContext.Provider>
  );
}

/**
 * Hook personalizado para usar el contexto de diagnóstico
 * @returns {Object} El contexto de diagnóstico
 */
export function useDiagnostic() {
  const context = useContext(DiagnosticContext);
  if (!context) {
    throw new Error('useDiagnostic debe usarse dentro de un DiagnosticProvider');
  }
  return context;
}

export default DiagnosticContext;
